import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, ConfigProvider, DatePicker, Input, Popover, Select, Tag } from "antd";
import type { Dayjs } from "dayjs";
import type { SessionData } from "./types";
import { YEAR_PRESETS, isCustomYear } from "./timeframe";
import {
  ArrowRight,
  Target,
  Sparkles,
  Clock,
  Layers,
  Eye,
  Mountain,
  Plus,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Trash2,
  RotateCcw,
  X,
  Pencil,
  Check,
  Users,
  UserCog,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import "./rb-globals.css";

const SERVER_URL = import.meta.env.VITE_REACT_APP_SERVER || "";

const buildingBlockIcons = [
  { icon: Eye, label: "Vision", color: "text-[#f37037]" },
  { icon: Target, label: "Mission", color: "text-[#2c1561]" },
  { icon: Mountain, label: "Peak Ambition", color: "text-[#802a88]" },
  { icon: Layers, label: "Objectives", color: "text-[#ef4269]" },
];

const phaseLabels: Record<number, string> = {
  2: "Data Gathering",
  3: "Synthesis",
  4: "Peak Ambition",
  5: "Objectives",
  6: "Gap Filling",
  7: "Draft Ready",
};

// Maps phase → antd Tag color preset
const phaseTagColor: Record<number, string | undefined> = {
  2: undefined,      // default (gray)
  3: "orange",
  4: "purple",
  5: "purple",
  6: "geekblue",
  7: "success",
};

function countFilledBlocks(blocks: SessionData["buildingBlocks"]): number {
  return Object.values(blocks).filter((b: any) => b.status !== "empty").length;
}

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function daysUntilPurge(deletedAt: string): number {
  const deleted = new Date(deletedAt).getTime();
  const purgeAt = deleted + 30 * 24 * 60 * 60 * 1000;
  return Math.max(0, Math.ceil((purgeAt - Date.now()) / (24 * 60 * 60 * 1000)));
}

export function RoadmapBuilder() {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState("");
  const [domain, setDomain] = useState("");
  const [context, setContext] = useState("");
  const [peakDate, setPeakDate] = useState<Dayjs | null>(null);
  const [yearStartMonth, setYearStartMonth] = useState(1);
  const [yearStartDay, setYearStartDay] = useState(1);
  const [yearPopoverOpen, setYearPopoverOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [sessions, setSessions] = useState<SessionData[]>([]);
  const [deletedSessions, setDeletedSessions] = useState<SessionData[]>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [hardDeletingId, setHardDeletingId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [roadmapsExpanded, setRoadmapsExpanded] = useState(true);
  const [advisingExpanded, setAdvisingExpanded] = useState(true);
  const [trashExpanded, setTrashExpanded] = useState(false);
  const [advisingSessions, setAdvisingSessions] = useState<{ id: string; companyName: string; updatedAt: string | null }[]>([]);

  const loadSessions = async () => {
    try {
      const [sessRes, delRes, advRes] = await Promise.all([
        fetch(`${SERVER_URL}/api/rb/sessions`, { credentials: "include" }),
        fetch(`${SERVER_URL}/api/rb/sessions/deleted/list`, { credentials: "include" }),
        fetch(`${SERVER_URL}/api/rb/advisor/sessions`, { credentials: "include" }),
      ]);
      if (sessRes.ok) setSessions(await sessRes.json());
      if (delRes.ok) setDeletedSessions(await delRes.json());
      if (advRes.ok) setAdvisingSessions(await advRes.json());
    } catch (e) {
      console.error("Failed to load sessions", e);
    }
  };

  useEffect(() => {
    loadSessions();
  }, []);

  const ownedSessions = sessions.filter((s) => !s.collaboratorRole);
  const sharedSessions = sessions.filter((s) => !!s.collaboratorRole);
  const hasSessions = ownedSessions.length > 0;
  const hasSharedSessions = sharedSessions.length > 0;
  const hasDeletedSessions = deletedSessions.length > 0;

  const handleRenameStart = (e: React.MouseEvent, session: SessionData) => {
    e.stopPropagation();
    setRenamingId(session.id);
    setRenameValue(session.companyName);
  };

  const handleRenameSave = async (sessionId: string) => {
    const trimmed = renameValue.trim();
    const current = sessions.find((s) => s.id === sessionId);
    if (!trimmed || trimmed === current?.companyName) {
      setRenamingId(null);
      return;
    }
    try {
      const res = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/rename`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ companyName: trimmed }),
        credentials: "include",
      });
      if (res.ok) {
        setSessions((prev) =>
          prev.map((s) => s.id === sessionId ? { ...s, companyName: trimmed } : s)
        );
      }
    } catch (e) {
      console.error("Failed to rename session", e);
    } finally {
      setRenamingId(null);
    }
  };

  const handleStart = async () => {
    if (!companyName.trim()) return;
    setIsLoading(true);
    try {
      const res = await fetch(`${SERVER_URL}/api/rb/sessions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          companyName: companyName.trim(),
          domain: domain.trim(),
          context: context.trim(),
          peakDate: peakDate ? peakDate.format("YYYY-MM-DD") : null,
          yearStartMonth,
          yearStartDay,
        }),
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to create session");
      const session = await res.json();
      navigate(`/roadmap/build_roadmap/${session.id}`);
    } catch {
      setIsLoading(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setDeletingId(id);
    try {
      await fetch(`${SERVER_URL}/api/rb/sessions/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      setSessions((prev) => prev.filter((s) => s.id !== id));
      loadSessions();
    } finally {
      setDeletingId(null);
    }
  };

  const handleRestore = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setRestoringId(id);
    try {
      await fetch(`${SERVER_URL}/api/rb/sessions/${id}/restore`, {
        method: "POST",
        credentials: "include",
      });
      loadSessions();
    } finally {
      setRestoringId(null);
    }
  };

  const handleHardDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!confirm("Permanently delete this roadmap? This cannot be undone.")) return;
    setHardDeletingId(id);
    try {
      await fetch(`${SERVER_URL}/api/rb/sessions/${id}/hard`, {
        method: "DELETE",
        credentials: "include",
      });
      setDeletedSessions((prev) => prev.filter((s) => s.id !== id));
    } finally {
      setHardDeletingId(null);
    }
  };

  return (
    <ConfigProvider theme={{ token: { colorPrimary: "#2c1561" } }}>
      {/* h-full + overflow-y-auto so the page scrolls inside the app layout
          rather than escaping it. BaseLayout already provides pb-40px for the fixed footer. */}
      <div className="rb-root" style={{ height: "100%", overflowY: "auto" }}>
        <main>
          {/* ── Hero ── */}
          <section className="max-w-5xl mx-auto px-4 py-10 md:py-14">
            <div className="grid md:grid-cols-2 gap-6 md:gap-8 items-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-sm font-medium text-primary">AI-Powered Strategic Planning</span>
                </div>

                <h1 className="font-serif text-3xl md:text-4xl tracking-tight leading-tight mb-3 text-foreground">
                  Build your strategic roadmap in{" "}
                  <span className="text-primary">~10 minutes</span>
                </h1>

                <p className="text-base text-muted-foreground mb-5 leading-relaxed">
                  Vo, your AI strategic thought partner, researches your subject, gathers your
                  insights, and drafts a complete 7-block roadmap from Vision to Quarterly
                  Objectives — for a company, a product, a book, or any initiative.
                </p>

                <div className="flex flex-wrap gap-4 mb-6">
                  {[
                    { icon: Clock, text: "~10 min to first draft" },
                    { icon: Layers, text: "7 strategic building blocks" },
                    { icon: Sparkles, text: "AI-reasoned assumptions" },
                  ].map((item) => (
                    <div key={item.text} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <item.icon className="h-4 w-4 text-primary/70" />
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-3">
                  {buildingBlockIcons.map((block, i) => (
                    <motion.div
                      key={block.label}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.3 + i * 0.1 }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-muted/50 text-xs font-medium text-foreground"
                    >
                      <block.icon className={`h-3.5 w-3.5 ${block.color}`} />
                      <span>{block.label}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                {!hasSessions || showNewForm ? (
                  <Card styles={{ body: { padding: 24 } }}>
                    <div className="mb-4">
                      <h2 className="font-serif text-lg mb-1 text-foreground">Get started</h2>
                      <p className="text-sm text-muted-foreground">
                        A company, a book, a product launch — anything you want to plan strategically.
                      </p>
                    </div>

                    <div className="space-y-3">
                      <div className="space-y-1.5">
                        <label htmlFor="company-name" className="text-xs font-medium text-foreground">
                          Roadmap for <span className="text-destructive">*</span>
                        </label>
                        <Input
                          id="company-name"
                          data-testid="input-company-name"
                          placeholder="e.g. Vojomo, Vojomo Book, Product Launch 2026"
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)}
                          onPressEnter={handleStart}
                          autoFocus={showNewForm}
                        />
                        <p className="text-xs text-muted-foreground">
                          Could be a company, book, product, project, or any initiative
                        </p>
                      </div>

                      <div className="space-y-1.5">
                        <label htmlFor="domain" className="text-xs font-medium text-foreground">
                          URL{" "}
                          <span className="text-muted-foreground font-normal">(optional)</span>
                        </label>
                        <Input
                          id="domain"
                          data-testid="input-domain"
                          placeholder="e.g. vojomo.com, linkedin.com/company/vojomo"
                          value={domain}
                          onChange={(e) => setDomain(e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground">
                          Any web presence — separate multiple URLs with commas
                        </p>
                      </div>

                      <div className="space-y-1.5">
                        <label htmlFor="context" className="text-xs font-medium text-foreground">
                          Context{" "}
                          <span className="text-muted-foreground font-normal">(optional)</span>
                        </label>
                        <Input.TextArea
                          id="context"
                          data-testid="input-linkedin"
                          placeholder="Give Vo a head start — describe what this is, what you're trying to achieve, who it's for, or anything else that would help set the stage."
                          value={context}
                          onChange={(e) => setContext(e.target.value)}
                          rows={3}
                          style={{ resize: "none" }}
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-foreground">
                          Target peak date{" "}
                          <span className="text-muted-foreground font-normal">(optional)</span>
                        </label>
                        <DatePicker
                          picker="month"
                          placeholder="e.g. Dec 2030"
                          value={peakDate}
                          onChange={(val) => setPeakDate(val)}
                          style={{ width: "100%" }}
                          format="MMM YYYY"
                          data-testid="input-peak-date"
                        />
                        <p className="text-xs text-muted-foreground">
                          When do you want to achieve this? Vo will anchor the roadmap around this date.
                        </p>
                      </div>

                      <Popover
                        open={yearPopoverOpen}
                        onOpenChange={setYearPopoverOpen}
                        trigger="click"
                        placement="bottomLeft"
                        title={<span className="text-xs font-medium">Year definition</span>}
                        content={
                          <div className="space-y-2" style={{ width: 260 }}>
                            <Select
                              style={{ width: "100%" }}
                              size="small"
                              value={
                                isCustomYear({ month: yearStartMonth, day: yearStartDay })
                                  ? "custom"
                                  : `${yearStartMonth}-${yearStartDay}`
                              }
                              onChange={(val) => {
                                if (val === "custom") return;
                                const [m, d] = val.split("-").map(Number);
                                setYearStartMonth(m);
                                setYearStartDay(d);
                              }}
                              options={[
                                ...YEAR_PRESETS.map(p => ({
                                  value: `${p.month}-${p.day}`,
                                  label: p.label,
                                })),
                                { value: "custom", label: "Custom…" },
                              ]}
                            />
                            {isCustomYear({ month: yearStartMonth, day: yearStartDay }) && (
                              <div className="flex items-center gap-2">
                                <Select
                                  style={{ flex: 1 }}
                                  size="small"
                                  value={yearStartMonth}
                                  onChange={setYearStartMonth}
                                  options={Array.from({ length: 12 }, (_, i) => ({
                                    value: i + 1,
                                    label: ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][i],
                                  }))}
                                />
                                <span className="text-xs text-muted-foreground">starts day</span>
                                <Input
                                  type="number"
                                  min={1}
                                  max={28}
                                  size="small"
                                  value={yearStartDay}
                                  onChange={(e) => setYearStartDay(Math.min(28, Math.max(1, Number(e.target.value))))}
                                  style={{ width: 52 }}
                                />
                              </div>
                            )}
                            <p className="text-xs text-muted-foreground">
                              Affects how Vo labels quarters and years in your roadmap.
                            </p>
                          </div>
                        }
                      >
                        <button
                          type="button"
                          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5 transition-colors"
                        >
                          <span>⚙</span>
                          Year definition
                          {!isCustomYear({ month: yearStartMonth, day: yearStartDay }) && (
                            <span className="text-muted-foreground/60">
                              ({YEAR_PRESETS.find(p => p.month === yearStartMonth && p.day === yearStartDay)?.label ?? "Custom"})
                            </span>
                          )}
                        </button>
                      </Popover>

                      <Button
                        type="primary"
                        block
                        style={{ marginTop: 4, height: 36 }}
                        onClick={handleStart}
                        disabled={!companyName.trim() || isLoading}
                        data-testid="button-start-roadmap"
                      >
                        {isLoading ? (
                          <span className="flex items-center gap-2">
                            <span className="h-4 w-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                            Starting session...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            Build a draft roadmap
                            <ArrowRight className="h-4 w-4" />
                          </span>
                        )}
                      </Button>

                      {hasSessions && (
                        <Button
                          type="text"
                          block
                          onClick={() => setShowNewForm(false)}
                          style={{ color: "#64748b" }}
                        >
                          ← Back to my roadmaps
                        </Button>
                      )}
                    </div>
                  </Card>
                ) : (
                  <Card
                    styles={{
                      body: {
                        padding: 24,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        gap: 12,
                        minHeight: 200,
                        cursor: "pointer",
                      },
                    }}
                    className="hover:shadow-sm transition-all"
                    onClick={() => setShowNewForm(true)}
                  >
                    <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Plus className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-serif text-lg mb-1 text-foreground">Start a new roadmap</h3>
                      <p className="text-sm text-muted-foreground">
                        Build a strategic roadmap for a new company or project
                      </p>
                    </div>
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowNewForm(true);
                      }}
                      icon={<ArrowRight className="h-3.5 w-3.5" />}
                      iconPosition="end"
                    >
                      New roadmap
                    </Button>
                  </Card>
                )}
              </motion.div>
            </div>
          </section>

          {/* ── Existing Sessions ── */}
          {hasSessions && (
            <section className="border-t border-border">
              <div className="max-w-5xl mx-auto px-4 py-6">
                {/* Section header — clickable div instead of raw <button> */}
                <div
                  className="flex items-center justify-between cursor-pointer select-none mb-0"
                  onClick={() => setRoadmapsExpanded((v) => !v)}
                  role="button"
                  aria-expanded={roadmapsExpanded}
                >
                  <div className="flex items-center gap-3">
                    <h2 className="font-serif text-xl text-foreground" style={{ margin: 0 }}>
                      Your roadmaps
                    </h2>
                    <Tag>
                      {ownedSessions.length} {ownedSessions.length === 1 ? "roadmap" : "roadmaps"}
                    </Tag>
                  </div>
                  <button className="icon-button-muted" tabIndex={-1} aria-hidden>
                    {roadmapsExpanded
                      ? <ChevronUp className="icon-16" />
                      : <ChevronDown className="icon-16" />}
                  </button>
                </div>

                <AnimatePresence initial={false}>
                  {roadmapsExpanded && (
                    <motion.div
                      key="roadmaps-grid"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: "easeInOut" }}
                      style={{ overflow: "hidden" }}
                    >
                      <p className="text-sm text-muted-foreground mt-2 mb-4">
                        Pick up where you left off
                      </p>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {ownedSessions.map((session, i) => {
                          const filled = countFilledBlocks(session.buildingBlocks);
                          const phase = session.currentPhase ?? 2;
                          const tagColor = phaseTagColor[phase];
                          const phaseName = phaseLabels[phase] ?? "In Progress";
                          const progressPct = Math.round((filled / 7) * 100);

                          return (
                            <motion.div
                              key={session.id}
                              initial={{ opacity: 0, y: 12 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: i * 0.05 }}
                            >
                              <Card
                                styles={{ body: { padding: 20 } }}
                                className="hover:shadow-md transition-all duration-200 cursor-pointer"
                                onClick={() => navigate(`/roadmap/build_roadmap/${session.id}`)}
                              >
                                <div className="flex items-start justify-between gap-2 mb-3">
                                  <div className="min-w-0 flex-1">
                                    {renamingId === session.id ? (
                                      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                        <input
                                          className="font-serif text-base font-medium leading-tight text-foreground border-b-2 border-primary bg-transparent outline-none flex-1 min-w-0 py-0"
                                          value={renameValue}
                                          onChange={(e) => setRenameValue(e.target.value)}
                                          onBlur={() => handleRenameSave(session.id)}
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter") handleRenameSave(session.id);
                                            if (e.key === "Escape") setRenamingId(null);
                                          }}
                                          autoFocus
                                        />
                                        <button
                                          className="icon-button-muted shrink-0"
                                          onMouseDown={(e) => { e.preventDefault(); handleRenameSave(session.id); }}
                                          title="Save"
                                        >
                                          <Check className="icon-14" />
                                        </button>
                                      </div>
                                    ) : (
                                      <h3 className="font-serif text-base font-medium truncate leading-tight text-foreground" style={{ margin: 0 }}>
                                        {session.companyName}
                                      </h3>
                                    )}
                                    {session.linkType === "solo_contribution" && session.parentSessionName ? (
                                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                                        Contribution to: {session.parentSessionName}
                                      </p>
                                    ) : session.domain ? (
                                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                                        {session.domain}
                                      </p>
                                    ) : null}
                                  </div>
                                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
                                    {session.linkType === "solo_contribution" && (
                                      <Tag color="purple" style={{ margin: 0 }}>
                                        Solo Contribution
                                      </Tag>
                                    )}
                                    {session.linkType !== "solo_contribution" && (
                                      <Tag color={tagColor} style={{ margin: 0 }}>
                                        {phaseName}
                                      </Tag>
                                    )}
                                  </div>
                                </div>

                                <div className="mb-3">
                                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
                                    <span>Blocks completed</span>
                                    <span>{filled} / 7</span>
                                  </div>
                                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-primary rounded-full transition-all duration-500"
                                      style={{ width: `${progressPct}%` }}
                                    />
                                  </div>
                                </div>

                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-muted-foreground">
                                    {relativeTime(session.updatedAt)}
                                  </span>
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={(e) => handleRenameStart(e, session)}
                                      className="icon-button-muted"
                                      title="Rename"
                                    >
                                      <Pencil className="icon-14" />
                                    </button>
                                    <button
                                      onClick={(e) => handleDelete(e, session.id)}
                                      disabled={deletingId === session.id}
                                      className="icon-button-muted"
                                      title="Move to trash"
                                    >
                                      <Trash2 className="icon-14" />
                                    </button>
                                    <span className="text-xs font-medium text-primary/60 flex items-center gap-0.5">
                                      Continue <ChevronRight className="h-3.5 w-3.5" />
                                    </span>
                                  </div>
                                </div>
                              </Card>
                            </motion.div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </section>
          )}

          {/* ── Shared With You ── */}
          {hasSharedSessions && (
            <section className="border-t border-border">
              <div className="max-w-5xl mx-auto px-4 py-6">
                <div className="flex items-center gap-3 mb-2">
                  <Users className="h-4 w-4 text-primary" />
                  <h2 className="font-serif text-xl text-foreground" style={{ margin: 0 }}>
                    Shared with you
                  </h2>
                  <Tag>
                    {sharedSessions.length} {sharedSessions.length === 1 ? "roadmap" : "roadmaps"}
                  </Tag>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Roadmaps others have invited you to collaborate on
                </p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {sharedSessions.map((session, i) => {
                    const filled = countFilledBlocks(session.buildingBlocks);
                    const progressPct = Math.round((filled / 7) * 100);
                    const roleLabel =
                      session.collaboratorRole === "view"
                        ? "View"
                        : session.collaboratorRole === "comment"
                        ? "Comment"
                        : "Solo Contributor";
                    const roleColor =
                      session.collaboratorRole === "view"
                        ? "default"
                        : session.collaboratorRole === "comment"
                        ? "processing"
                        : "purple";

                    return (
                      <motion.div
                        key={session.id}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.05 }}
                      >
                        <Card
                          styles={{ body: { padding: 20 } }}
                          className="hover:shadow-md transition-all duration-200 cursor-pointer"
                          onClick={() => navigate(`/roadmap/build_roadmap/${session.id}`)}
                        >
                          <div className="flex items-start justify-between gap-2 mb-3">
                            <h3 className="font-medium text-sm leading-snug line-clamp-2" style={{ margin: 0 }}>
                              {session.companyName}
                            </h3>
                            <Tag color={roleColor} className="text-xs shrink-0">
                              {roleLabel}
                            </Tag>
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <div className="flex-1 bg-slate-100 rounded-full h-1">
                              <div
                                className="bg-primary h-1 rounded-full"
                                style={{ width: `${progressPct}%` }}
                              />
                            </div>
                            <span className="text-xs text-muted-foreground">{filled}/7 blocks</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">
                            Updated {relativeTime(session.updatedAt)}
                          </p>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </section>
          )}

          {/* ── Sessions I'm Advising ── */}
          {advisingSessions.length > 0 && (
            <section className="border-t border-border">
              <div className="max-w-5xl mx-auto px-4 py-6">
                <div
                  className="flex items-center justify-between cursor-pointer select-none mb-0"
                  onClick={() => setAdvisingExpanded((v) => !v)}
                  role="button"
                  aria-expanded={advisingExpanded}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <UserCog className="h-4 w-4 text-primary/70" />
                      <h2 className="font-serif text-xl text-foreground" style={{ margin: 0 }}>
                        Sessions I'm advising
                      </h2>
                    </div>
                    <Tag color="purple">
                      {advisingSessions.length} {advisingSessions.length === 1 ? "session" : "sessions"}
                    </Tag>
                  </div>
                  <button className="icon-button-muted" tabIndex={-1} aria-hidden>
                    {advisingExpanded
                      ? <ChevronUp className="icon-16" />
                      : <ChevronDown className="icon-16" />}
                  </button>
                </div>

                <AnimatePresence initial={false}>
                  {advisingExpanded && (
                    <motion.div
                      key="advising-grid"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: "easeInOut" }}
                      style={{ overflow: "hidden" }}
                    >
                      <p className="text-sm text-muted-foreground mt-2 mb-4">
                        Sessions where you've been added as an Advisor
                      </p>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {advisingSessions.map((session, i) => (
                          <motion.div
                            key={session.id}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.05 }}
                          >
                            <Card
                              styles={{ body: { padding: 20 } }}
                              className="hover:shadow-md transition-all duration-200 cursor-pointer"
                              onClick={() => navigate(`/roadmap/build_roadmap/${session.id}`)}
                            >
                              <div className="flex items-start justify-between gap-2 mb-3">
                                <div className="min-w-0 flex-1">
                                  <h3 className="font-serif text-base font-medium truncate leading-tight text-foreground" style={{ margin: 0 }}>
                                    {session.companyName}
                                  </h3>
                                </div>
                                <Tag color="purple" className="shrink-0 text-[10px] py-0">Advisor</Tag>
                              </div>
                              <div className="flex items-center justify-between mt-3">
                                <span className="text-xs text-muted-foreground">
                                  {session.updatedAt ? `Updated ${relativeTime(session.updatedAt)}` : ""}
                                </span>
                                <span className="text-xs font-medium text-primary/60 flex items-center gap-0.5">
                                  Open <ChevronRight className="h-3.5 w-3.5" />
                                </span>
                              </div>
                            </Card>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </section>
          )}

          {/* ── Deleted Sessions (Trash) ── */}
          {hasDeletedSessions && (
            <section className="border-t border-border">
              <div className="max-w-5xl mx-auto px-4 py-5">
                <div
                  className="flex items-center justify-between cursor-pointer select-none"
                  onClick={() => setTrashExpanded((v) => !v)}
                  role="button"
                  aria-expanded={trashExpanded}
                >
                  <div className="flex items-center gap-3">
                    <h2 className="font-serif text-base text-muted-foreground" style={{ margin: 0 }}>
                      Deleted roadmaps
                    </h2>
                    <Tag>
                      {deletedSessions.length} {deletedSessions.length === 1 ? "roadmap" : "roadmaps"}
                    </Tag>
                  </div>
                  <button className="icon-button-muted" tabIndex={-1} aria-hidden>
                    {trashExpanded
                      ? <ChevronUp className="icon-16" />
                      : <ChevronDown className="icon-16" />}
                  </button>
                </div>

                <AnimatePresence initial={false}>
                  {trashExpanded && (
                    <motion.div
                      key="trash-grid"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: "easeInOut" }}
                      style={{ overflow: "hidden" }}
                    >
                      <p className="text-sm text-muted-foreground mt-2 mb-4">
                        Deleted roadmaps are permanently removed after 30 days.
                      </p>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {deletedSessions.map((session, i) => {
                          const days = session.deletedAt ? daysUntilPurge(session.deletedAt) : 30;

                          return (
                            <motion.div
                              key={session.id}
                              initial={{ opacity: 0, y: 12 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: i * 0.05 }}
                            >
                              <Card styles={{ body: { padding: 20 } }} style={{ opacity: 0.7 }}>
                                <div className="flex items-start justify-between gap-2 mb-3">
                                  <div className="min-w-0">
                                    <h3 className="font-serif text-base font-medium truncate leading-tight text-foreground" style={{ margin: 0 }}>
                                      {session.companyName}
                                    </h3>
                                    {session.domain && (
                                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                                        {session.domain}
                                      </p>
                                    )}
                                  </div>
                                  <Tag color="error">
                                    {days}d left
                                  </Tag>
                                </div>

                                <div className="flex items-center justify-between mt-3">
                                  <span className="text-xs text-muted-foreground">
                                    Deleted {session.deletedAt ? relativeTime(session.deletedAt) : ""}
                                  </span>
                                  <div className="flex items-center gap-1">
                                    <Button
                                      type="text"
                                      size="small"
                                      icon={<RotateCcw className="h-3 w-3" />}
                                      loading={restoringId === session.id}
                                      onClick={(e) => handleRestore(e, session.id)}
                                      title="Restore roadmap"
                                    >
                                      Restore
                                    </Button>
                                    <Button
                                      type="text"
                                      size="small"
                                      danger
                                      icon={<X className="h-3 w-3" />}
                                      loading={hardDeletingId === session.id}
                                      onClick={(e) => handleHardDelete(e, session.id)}
                                      title="Delete permanently"
                                    >
                                      Delete forever
                                    </Button>
                                  </div>
                                </div>
                              </Card>
                            </motion.div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </section>
          )}

          {/* ── How it works ── */}
          <section className="border-t border-border bg-muted/30">
            <div className="max-w-5xl mx-auto px-4 py-10">
              <h2 className="font-serif text-xl text-center mb-6 text-foreground">How it works</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  {
                    step: "01",
                    title: "Tell Vo what you're building toward",
                    desc: "Enter a name and any context. Share documents, links, or just talk freely about your vision.",
                  },
                  {
                    step: "02",
                    title: "Vo researches & synthesizes",
                    desc: "While you share, Vo pulls public data and maps everything to your 7 strategic building blocks.",
                  },
                  {
                    step: "03",
                    title: "Review your draft roadmap",
                    desc: "Get a structured roadmap with confirmed data, AI-reasoned assumptions, and clear gaps to fill.",
                  },
                ].map((item) => (
                  <div key={item.step} className="space-y-3">
                    <span className="text-xs font-mono font-bold text-primary">{item.step}</span>
                    <h3 className="font-serif text-lg text-foreground">{item.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>
      </div>
    </ConfigProvider>
  );
}
